import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomerAuthContext = createContext(null);

export const useCustomerAuth = () => {
  const context = useContext(CustomerAuthContext);
  if (!context) {
    throw new Error('useCustomerAuth must be used within CustomerAuthProvider');
  }
  return context;
};

export const CustomerAuthProvider = ({ children }) => {
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('customerToken'));

  useEffect(() => {
    if (token) {
      fetchCustomerProfile();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchCustomerProfile = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomer(response.data);
    } catch (error) {
      // Token invalid or expired
      localStorage.removeItem('customerToken');
      setToken(null);
      setCustomer(null);
    } finally {
      setLoading(false);
    }
  };

  const register = async (name, email, password, phone = '') => {
    try {
      const response = await axios.post(`${API}/auth/register`, {
        name, email, password, phone
      });
      const { token: newToken, user } = response.data;
      localStorage.setItem('customerToken', newToken);
      setToken(newToken);
      setCustomer(user);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Registration failed' 
      };
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { token: newToken, user } = response.data;
      localStorage.setItem('customerToken', newToken);
      setToken(newToken);
      setCustomer(user);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Invalid email or password' 
      };
    }
  };

  const googleLogin = async (sessionId) => {
    try {
      const response = await axios.post(`${API}/auth/google/callback`, { session_id: sessionId });
      const { token: newToken, user } = response.data;
      localStorage.setItem('customerToken', newToken);
      setToken(newToken);
      setCustomer(user);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Google login failed' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('customerToken');
    setToken(null);
    setCustomer(null);
  };

  const updateProfile = async (data) => {
    try {
      const response = await axios.put(`${API}/auth/profile`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomer(response.data);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Update failed' 
      };
    }
  };

  return (
    <CustomerAuthContext.Provider value={{
      customer,
      token,
      loading,
      isAuthenticated: !!customer,
      register,
      login,
      googleLogin,
      logout,
      updateProfile,
      refreshProfile: fetchCustomerProfile
    }}>
      {children}
    </CustomerAuthContext.Provider>
  );
};
